import {
  InteractionRequiredAuthError,
  PublicClientApplication,
} from '@azure/msal-browser';
import type { AccountInfo, AuthenticationResult } from '@azure/msal-browser';
import type { RuntimeConfig } from './config';

/**
 * Entra sign-in, by REDIRECT. There is no popup lane and there should not be:
 * iOS Safari blocks popups by default and a meaningful share of this product's
 * parents are on an iPad. A blocked popup fails as "nothing happened", which
 * is the least debuggable failure a sign-in button can have.
 *
 * What this module hands api/client.ts is an ACCESS token, not an id token.
 * That inverts the Cognito rule this codebase carried for months. On Cognito,
 * HttpUserPoolAuthorizer validated `aud` against the client id and only id
 * tokens carried `aud`. On Entra an id token's `aud` is the CLIENT and an
 * access token's `aud` is the API — azure/functions/auth.py decodes with
 * `audience=FAMILY_AUDIENCE`, which auth_setup.sh sets to the family app's
 * client id. Send the id token and you get a 401 that names nothing.
 */

/**
 * THE PLACEHOLDER CHECK USED TO LIVE HERE AND MUST NOT COME BACK.
 *
 * It was right about the danger — MSAL accepts any non-empty string as a client
 * id, so an unchecked placeholder surfaces as an opaque "unauthorized_client"
 * on Microsoft's own page after a full-page redirect, with nothing in this
 * app's console. It was wrong about the ERROR CLASS. A plain Error from here is
 * not a ConfigError, so boot.ts recorded `configFault: false` and App.tsx put
 * the "Sign out and start again" button under it — a button that discards the
 * diagnosis and lands the parent on a sign-in screen this module cannot serve,
 * because nothing below has run and `pca` is still null.
 *
 * config.ts rejects it before this function is ever called, with the appId
 * command in the message. One check, one sentence: a second guard here would
 * be a staler copy of that sentence and could only fire for a caller that
 * bypassed loadConfig(), of which there are none.
 */

let pca: PublicClientApplication | null = null;
let apiScopes: string[] = [];

/**
 * How the page load ended, as a value rather than as a thrown error.
 *
 * `refused` is the one that had to exist. Entra answers a cancelled sign-in, a
 * failed MFA, a conditional-access block and a scope the registration does not
 * expose the same way: it sends the browser back here with `#error=` in the
 * fragment, which makes handleRedirectPromise() REJECT. That used to escape
 * configureAuth as a boot failure, and a boot failure is not recoverable from
 * inside this app — the parent got a full-screen panel blaming /config.json,
 * with no button on it and a stale account still in localStorage that put them
 * straight back into the same redirect on the next load. A refusal is not a
 * broken deployment; it is a sign-in that did not happen, and it belongs on the
 * sign-in screen.
 */
export type AuthBoot =
  | { kind: 'signed-in'; email: string }
  | { kind: 'signed-out' }
  | { kind: 'refused'; message: string };

/**
 * Told when a session that LOOKED alive turns out to be dead, so that the app
 * can put the parent back on the sign-in screen rather than leave them staring
 * at a screen whose every call 401s.
 *
 * A listener rather than a direct state write because this module is imported
 * by api/client.ts, which is imported by every screen; reaching into the store
 * from here would put a UI decision in the one module the tests load in
 * isolation.
 *
 * Latched: three screens fire two calls at once on mount, all of which fail
 * together when the refresh token has expired, and the parent should be told
 * once.
 */
let sessionEndListener: ((reason: string) => void) | null = null;
let sessionEndAnnounced = false;

export function whenSessionEnds(listener: (reason: string) => void): void {
  sessionEndListener = listener;
}

function announceSessionEnd(reason: string): void {
  if (sessionEndAnnounced) return;
  sessionEndAnnounced = true;
  sessionEndListener?.(reason);
}

function reason(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

/**
 * Constructed here and NEVER at module scope. MSAL reaches for crypto.subtle
 * in its constructor, which jsdom does not provide, so a module-scope instance
 * would throw during import — and screens/ChildMode.test.tsx and
 * screens/Summary.test.tsx pull this module in transitively through
 * api/endpoints. Module-scope construction turns those eight passing tests red
 * for a reason that has nothing to do with what they test.
 *
 * Awaits in order, and the order is load-bearing:
 *
 *   initialize()            MSAL v3+ throws from every other method without it
 *   handleRedirectPromise() redeems the #code= fragment Entra just sent back.
 *                           Skip it and the fragment is never exchanged, the
 *                           app finds no account, and it bounces to sign-in
 *                           forever with no error anywhere.
 *
 * THROWS only for a deployment that cannot work at all — MSAL refusing to
 * construct or to initialize. Everything Entra itself can say no to comes back
 * as `refused` — see AuthBoot. A config that cannot work never reaches here:
 * loadConfig() rejects it first, as a ConfigError, for the reason above.
 */
export async function configureAuth(config: RuntimeConfig): Promise<AuthBoot> {
  apiScopes = [config.apiScope];
  pca = new PublicClientApplication({
    auth: {
      clientId: config.clientId,
      authority: `https://login.microsoftonline.com/${config.tenantId}`,
      // MSAL matches this byte-for-byte against the registration's SPA
      // redirectUris — no wildcards, no port tolerance. entra_bootstrap.sh
      // registers http://localhost:5173, which is why vite.config.ts serves
      // this app there and not on a port of its own choosing.
      redirectUri: window.location.origin,
      navigateToLoginRequestUrl: false,
    },
    cache: {
      // MSAL defaults to sessionStorage, which would sign a parent out on
      // every tab close. Amplify defaulted to localStorage and boot() below
      // still assumes a session survives a reload.
      cacheLocation: 'localStorage',
    },
  });

  await pca.initialize();
  sessionEndAnnounced = false;

  let redirect: AuthenticationResult | null = null;
  try {
    redirect = await pca.handleRedirectPromise();
  } catch (err) {
    // THE LOOP BREAKER, and it is the clearCache() rather than the return
    // value. MSAL has already stripped the #error= fragment by the time it
    // rejects, so a reload finds nothing to redeem — but the ACCOUNT ENTITY
    // from whenever the parent last signed in successfully survives in
    // localStorage, and an account is all boot looks at. Leave it and the app
    // decides it is signed in, a screen asks for a token, the silent renewal
    // fails, and the parent is sent back to the same refusal. Forever, with
    // the only control that could have cleared it (sign out, on the top bar)
    // one screen behind the redirect.
    await endSession();
    return { kind: 'refused', message: reason(err) };
  }

  const account: AccountInfo | null = redirect?.account ?? pca.getAllAccounts()[0] ?? null;
  pca.setActiveAccount(account);
  if (!account) return { kind: 'signed-out' };
  return { kind: 'signed-in', email: account.username || account.name || 'signed in' };
}

function instance(): PublicClientApplication {
  if (!pca) throw new Error('auth used before configureAuth() — boot order changed');
  return pca;
}

/**
 * Leaves the page. Never resolves in practice: the browser is already on its
 * way to login.microsoftonline.com by the time the promise would settle.
 *
 * The API scope is requested HERE, at sign-in, rather than left to the first
 * silent acquire. Asking for only openid/profile yields a session that looks
 * perfectly healthy and a token the API rejects — the mistake is invisible
 * until someone base64-decodes the `aud` claim.
 */
export async function beginSignIn(): Promise<void> {
  await instance().loginRedirect({ scopes: apiScopes });
}

/**
 * No longer a network call. Entra's account cache carries the username, so
 * this is a synchronous read — boot's "skip the sign-in screen" check costs
 * nothing now.
 */
export function currentUserEmail(): string | null {
  return pca?.getActiveAccount()?.username ?? null;
}

/**
 * LOCAL sign-out. clearCache() drops this app's tokens; logoutRedirect() would
 * sign the parent out of MICROSOFT on that browser, which on a shared family
 * iPad logs them out of everything else too. It also navigates away, so
 * App.tsx's .finally() chain that resets the wizard would never run.
 *
 * Three callers now, and the two that are not the sign-out button matter more:
 * a refused return from Entra and a dead refresh token both leave an account
 * entity behind that outlives the credentials it names. That entity is the only
 * thing boot looks at, so not dropping it is what turned a cancelled sign-in
 * into a loop with no way out of it.
 */
export async function endSession(): Promise<void> {
  const msal = pca;
  if (!msal) return;
  await msal.clearCache();
  msal.setActiveAccount(null);
}

/**
 * The only token producer in the SPA. api/client.ts is the only caller.
 *
 * NULL MEANS ONE THING: there is no usable session. Either nobody is signed in,
 * or the session that looked alive is dead — the caller cannot act differently
 * on the two, and it does not have to, because this function has already told
 * the app to go back to the sign-in screen.
 *
 * IT DOES NOT REDIRECT, and that is a deliberate reversal. Entra issues SPA
 * refresh tokens with a 24-hour NON-RENEWABLE lifetime (against Cognito's
 * 30-day default), so a parent who uses the app daily lands in this catch
 * roughly every day — and this runs from a mount effect, not from a click. It
 * used to answer that with acquireTokenRedirect(), which:
 *
 *   * navigated the whole page to login.microsoftonline.com with NO USER
 *     GESTURE, which is the exact behaviour SignIn.tsx refuses to do on mount
 *     and SignIn.test.tsx guards against, reintroduced one screen later;
 *   * threw BrowserAuthError(interaction_in_progress) — a different class, so
 *     it was rethrown, not caught — whenever a second call was already
 *     redirecting. Library, CommissionProgress and Summary each fire two authed
 *     calls on mount, so an expiry on any of them painted "Interaction is
 *     currently in progress" as the page navigated away;
 *   * left the parent one cancelled sign-in away from an unrecoverable loop,
 *     because the failed return trip landed in configureAuth as a boot error.
 *
 * Dropping the dead session is what makes the next cold load land on the
 * sign-in screen instead of repeating this.
 */
export async function apiToken(): Promise<string | null> {
  const msal = instance();
  const account = msal.getActiveAccount();
  if (!account) return null;

  try {
    const result = await msal.acquireTokenSilent({ account, scopes: apiScopes });
    return result.accessToken;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      await endSession();
      announceSessionEnd(reason(err));
      return null;
    }
    throw err;
  }
}
